import { Settings, FileText, Users, Shield, Package, type LucideIcon, Wrench, FileSpreadsheet, TrendingUp, GraduationCap, FlaskConical, Target, ShoppingCart, UsersRound, Landmark, Car, Receipt, Banknote, ClipboardList, Clock, ShieldAlert, ArrowLeftRight, ScanLine, Wallet, Coins, Calculator, MessagesSquare, Scale, BarChart3, Combine, Activity, Gauge, Share2, FileStack, Percent, Copy, CalendarClock, Goal, Megaphone } from 'lucide-react';

export interface NavItem {
    label: string;
    path: string;
    icon: LucideIcon;
    pageKey: string;
    adminOnly?: boolean;
}

export interface NavGroup {
    label: string;
    icon: LucideIcon;
    collapsible: boolean;
    items: NavItem[];
}

/**
 * Main navigation groups shown at the top of the sidebar.
 */
export const mainNavGroups: NavGroup[] = [
    {
        label: 'Report',
        icon: FileText,
        collapsible: false,
        items: [
            {
                label: 'BAB Report',
                path: '/bab-report',
                icon: BarChart3,
                pageKey: 'bab-report',
            },
            {
                label: 'BSC Costs (BAB)',
                path: '/bab-bsc-costs',
                icon: Share2,
                pageKey: 'bab-bsc-costs',
            },
            {
                label: 'Financial Statement',
                path: '/financial-statement',
                icon: FileSpreadsheet,
                pageKey: 'financial-statement',
            },
            {
                label: 'Power BI Usage',
                path: '/powerbi-usage',
                icon: Activity,
                pageKey: 'powerbi-usage',
            },
            {
                label: 'App Usage',
                path: '/app-usage',
                icon: Gauge,
                pageKey: 'app-usage',
            },
            {
                label: 'DocuWare Invoices',
                path: '/docuware-invoices',
                icon: FileStack,
                pageKey: 'docuware-invoices',
            },
            {
                label: 'VAT Reporting',
                path: '/vat-reporting',
                icon: Percent,
                pageKey: 'vat-reporting',
            },
            {
                label: 'Frame Order Tracking',
                path: '/frame-order-tracking',
                icon: FileText,
                pageKey: 'frame-order-tracking',
            },
            {
                label: 'Sales Controlling',
                path: '/sales-controlling',
                icon: TrendingUp,
                pageKey: 'sales-controlling',
            },
            {
                label: 'Strategic KPIs',
                path: '/strategic-kpis',
                icon: Goal,
                pageKey: 'strategic-kpis',
            },
            {
                label: 'Margin Corrections',
                path: '/margin-corrections',
                icon: Scale,
                pageKey: 'margin-corrections',
            },
            {
                label: 'Purchase Reporting',
                path: '/purchase-reporting',
                icon: ShoppingCart,
                pageKey: 'purchase-reporting',
            },
            {
                label: 'Slow & No Mover',
                path: '/slow-no-mover',
                icon: Package,
                pageKey: 'slow-no-mover',
            },
            {
                label: 'Aged Stock by Lot',
                path: '/aged-stock',
                icon: Clock,
                pageKey: 'aged-stock',
            },
            {
                label: 'Consolidated Business',
                path: '/consolidated-business',
                icon: Combine,
                pageKey: 'consolidated-business',
            },
            {
                label: 'Credit Limits',
                path: '/credit-limits',
                icon: Shield,
                pageKey: 'credit-limits',
            },
            {
                label: 'Accounts Receivable',
                path: '/accounts-receivable',
                icon: Coins,
                pageKey: 'accounts-receivable',
            },
            {
                label: 'Credit Note Control',
                path: '/credit-notes',
                icon: ClipboardList,
                pageKey: 'credit-notes',
            },
            {
                label: 'Customer Duplicates',
                path: '/customer-duplicates',
                icon: Copy,
                pageKey: 'customer-duplicates',
            },
            {
                label: 'SPC Classification',
                path: '/spc-classification',
                icon: Target,
                pageKey: 'spc-classification',
            },
            {
                label: 'Vendor Cost Analysis',
                path: '/vendor-cost-analysis',
                icon: Receipt,
                pageKey: 'vendor-cost-analysis',
            },
            {
                label: 'Cash Cockpit',
                path: '/cash-cockpit',
                icon: Banknote,
                pageKey: 'cash-cockpit',
            },
            {
                label: 'Payroll Review',
                path: '/payroll-review',
                icon: ShieldAlert,
                pageKey: 'payroll-review',
            },
            {
                label: 'TimeRocket Hours',
                path: '/timerocket-hours',
                icon: CalendarClock,
                pageKey: 'timerocket-hours',
            },
        ],
    },
    {
        label: 'Data Entry',
        icon: FileText,
        collapsible: false,
        items: [
            {
                label: 'Bank Balance',
                path: '/bank-balance',
                icon: Landmark,
                pageKey: 'bank-balance',
            },
            {
                label: 'Payment Reconciliation',
                path: '/payment-reconciliation',
                icon: ArrowLeftRight,
                pageKey: 'payment-reconciliation',
            },
            {
                label: 'Expense Capture',
                path: '/expense-capture',
                icon: ScanLine,
                pageKey: 'expense-capture',
            },
            {
                label: 'Expense Management',
                path: '/expense-management',
                icon: Wallet,
                pageKey: 'expense-management',
            },
            {
                label: 'Lohnbuchungsbeleg CH',
                path: '/lohn-buchungsbeleg',
                icon: Calculator,
                pageKey: 'lohn-buchungsbeleg',
            },
            {
                label: 'Training Log',
                path: '/training-log',
                icon: GraduationCap,
                pageKey: 'training-log',
            },
            {
                label: 'Company Cars',
                path: '/company-cars',
                icon: Car,
                pageKey: 'company-cars',
            },
        ],
    },
    {
        label: 'Tools',
        icon: Wrench,
        collapsible: false,
        items: [
            {
                label: 'MSDS Cover Page',
                path: '/msds-coverpage',
                icon: FlaskConical,
                pageKey: 'msds-coverpage',
            },
            {
                label: 'SDS Assistant',
                path: '/sds-assistant',
                icon: MessagesSquare,
                pageKey: 'sds-assistant',
            },
            // Pure Customer Search moved to pure.dolderapps.com on 2026-09-12
            // (migration 200). The entry is removed here as well as deactivated in the
            // database — the navigation filters on the page row, so one would have been
            // enough, and two means the item cannot come back by a data accident.
            // Customer 360 moved to customer360.dolderapps.com on 2026-09-21
            // (migration 209, phase P8a). Same two halves as Pure Customer Search
            // above: the entry goes from here AND the page row goes IsActive = 0.
            // Unlike that move, the grants on page 11 are left standing — 19 people
            // hold CanView and CanEdit there, and the rollback for this phase has to
            // be a flag flip rather than a restore.
            //
            // Supplier Order Management and its Product Master Data (under
            // Configuration) moved to som.dolderapps.com on 2026-09-24 (migration 214,
            // SOM phase P7). Same two halves again: both entries go from here AND both
            // page rows go IsActive = 0, grants left standing. Supplier Product
            // Managers (/supplier-pm-config) stays in this app.
            //
            // Visit Report below stays, and on this day it becomes visible to those
            // 19 for the first time: migration 209 grants them page 19 as well, so
            // that the "Open in mobile view" link in the new app leads somewhere.
            {
                label: 'Visit Report',
                path: '/visit-report',
                icon: ClipboardList,
                pageKey: 'visit-report',
            },
        ],
    },
    {
        label: 'Configuration',
        icon: Wrench,
        collapsible: false,
        items: [
            {
                label: 'Sales Team Config',
                path: '/sales-team-config',
                icon: Users,
                pageKey: 'sales-team-config',
            },
            {
                label: 'Supplier Product Managers',
                path: '/supplier-pm-config',
                icon: UsersRound,
                pageKey: 'supplier-pm-config',
            },
            {
                label: 'Notices',
                path: '/notices',
                icon: Megaphone,
                pageKey: 'notices',
            },
        ],
    },
];

/**
 * Settings navigation group shown at the bottom of the sidebar.
 */
export const settingsNavGroup: NavGroup = {
    label: 'Settings',
    icon: Settings,
    collapsible: true,
    items: [
        {
            label: 'User Management',
            path: '/settings/users',
            icon: Users,
            pageKey: 'settings',
            adminOnly: true,
        },
        {
            label: 'Permission Matrix',
            path: '/settings/permissions',
            icon: Shield,
            pageKey: 'settings',
            adminOnly: true,
        },
    ],
};
